import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchClientCommunications,
  createClientCommunication,
  deleteClientCommunication,
  type CreateCommunicationParams,
} from '@/services/clientCommunicationsService';
import type { ClientCommunication } from '@/types/client';
import { clientQueryKeys } from './useClients';

export const clientCommQueryKeys = {
  all: (clientId: string) => [...clientQueryKeys.all, 'communications', clientId] as const,
};

export const useFetchClientCommunications = (clientId: string | null) => {
  return useQuery<ClientCommunication[], Error>({
    queryKey: clientCommQueryKeys.all(clientId!),
    queryFn: () => fetchClientCommunications(clientId!),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useCreateClientCommunication = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation<ClientCommunication, Error, CreateCommunicationParams>({
    mutationFn: createClientCommunication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientCommQueryKeys.all(clientId) });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.activityFeed(clientId) });
    },
  });
};

export const useDeleteClientCommunication = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteClientCommunication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientCommQueryKeys.all(clientId) });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.activityFeed(clientId) });
    },
  });
};
